// Minimal accessible modal: overlay + ESC-to-close + focus the first
// focusable element on open. Not a full focus trap (deferred to Task
// 11 polish), but enough for the create-form flow.

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  width?: number | string;
  // When true: overlay click, ESC, and the X button are all no-ops.
  // Use this while a destructive mutation is in flight so the
  // operator can't accidentally bail out before the response
  // surfaces (e.g. tenant create's degraded-success banner).
  closeDisabled?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  width = 480,
  closeDisabled = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const tryClose = () => {
    if (!closeDisabled) onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") tryClose();
    };
    document.addEventListener("keydown", onKey);
    // Focus first focusable inside the dialog.
    const first = dialogRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button, [tabindex]:not([tabindex='-1'])",
    );
    first?.focus();
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tryClose is stable per render; closeDisabled flips intent, not handler identity.
  }, [open, onClose, closeDisabled]);

  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={tryClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14, 17, 22, 0.7)",
        display: "grid",
        placeItems: "center",
        padding: 16,
        zIndex: 50,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: 20,
          color: "var(--text)",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h2 id="modal-title" style={{ margin: 0, fontSize: "1rem" }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={tryClose}
            disabled={closeDisabled}
            aria-label="Close"
            title={closeDisabled ? "Wait for the in-flight request to finish" : "Close"}
            style={{
              background: "transparent",
              border: "1px solid var(--border)",
              color: closeDisabled ? "var(--border)" : "var(--text-dim)",
              width: 28,
              height: 28,
              borderRadius: 4,
              cursor: closeDisabled ? "not-allowed" : "pointer",
              fontSize: "1rem",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
